import API from '../utils/api';
import { call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';

import {
    ON_LOAD_LOGIN_REQUESTED,
    ON_LOAD_LOGIN_SUCCESS,
    ON_LOAD_LOGIN_FAILED,
    LOGOUT_REQUESTED,
    LOGOUT_SUCCESS,
    LOGOUT_FAILED,
    POST_RESET_PASSWORD_REQUESTED,
    POST_RESET_PASSWORD_SUCCESS,
    POST_RESET_PASSWORD_FAILED,
    REFRESH_SESSION_REQUESTED,
    ADD_CELL_SUCCESS,
    UPDATE_CELL_SUCCESS,
    DELETE_CELL_SUCCESS,
    ADD_CELL_LEADER_SUCCESS,
    DELETE_CELL_LEADER_SUCCESS,
    ADD_ADMIN_SUCCESS,
    UPDATE_ADMIN_SUCCESS,
    DELETE_ADMIN_SUCCESS,
} from '../actions/types';

import {
    onLoginSuccess,
    onLoginFailed,
    onLogoutSuccess,
    onLogoutFailed,
    onResetPasswordSuccess,
    onResetPasswordFailed,
    refreshSessionRequested,
    refreshSessionSuccess,
    refreshSessionFailed,
} from '../actions/authAction';

/* ----- LOGIN ----- */
// payload: { email, password } for password sign-in, or { provider, idToken } for SSO.
export function* onLoadLogin(action) {
    const { provider, idToken } = action.payload || {};
    try {
        const response = provider
            ? yield call(API.login, `auth/${provider}`, { idToken })
            : yield call(API.login, 'auth/login', action.payload);
        if (response && response.token) {
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(response));
            yield put(onLoginSuccess(response));
        } else {
            const fallback = provider ? 'Sign-in failed. Please try again.' : 'Username or password is incorrect';
            yield put(onLoginFailed(response?.data?.msg || fallback));
        }
    } catch (error) {
        yield put(onLoginFailed('Something went wrong. Please try again.'));
    }
}

export function* watchOnLoadLogin() {
    yield takeLatest(ON_LOAD_LOGIN_REQUESTED, onLoadLogin);
}

/* ----- LOGOUT ----- */
export function* onLogout() {
    try {
        let data = JSON.parse(localStorage.getItem('user'));
        delete data.token;
        yield call(API.postData, 'auth/logout', data);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        yield put(onLogoutSuccess());
    } catch (error) {
        yield put(onLogoutFailed(error.response));
    }
}

export function* watchOnLogout() {
    yield takeLatest(LOGOUT_REQUESTED, onLogout);
}

/* ----- RESET PASSWORD ----- */
export function* onResetPassword(action) {
    try {
        const response = yield call(API.resetPassword, action.payload);
        yield put(onResetPasswordSuccess(response.data));
    } catch (error) {
        yield put(onResetPasswordFailed(error.response));
    }
}

/* ----- SESSION REFRESH ----- */
// The stored session (roles, led cells, token) is a snapshot from sign-in.
// Re-fetch it after anything that can change the user's own roles or cells,
// e.g. an admin deleting the only cell they lead.
export function* refreshSession() {
    if (!localStorage.getItem('token')) return;
    try {
        const response = yield call(API.getData, 'auth/session');
        if (response && response.token && response.id != null) {
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(response));
            yield put(refreshSessionSuccess(response));
        } else {
            yield put(refreshSessionFailed(response));
        }
    } catch (error) {
        yield put(refreshSessionFailed(error));
    }
}

export const SESSION_CHANGING_ACTIONS = [
    ADD_CELL_SUCCESS,
    UPDATE_CELL_SUCCESS,
    DELETE_CELL_SUCCESS,
    ADD_CELL_LEADER_SUCCESS,
    DELETE_CELL_LEADER_SUCCESS,
    ADD_ADMIN_SUCCESS,
    UPDATE_ADMIN_SUCCESS,
    DELETE_ADMIN_SUCCESS,
];

export function* requestSessionRefresh() {
    yield put(refreshSessionRequested());
}

export default function* authSaga() {
    yield takeLatest(REFRESH_SESSION_REQUESTED, refreshSession);
    yield takeEvery(SESSION_CHANGING_ACTIONS, requestSessionRefresh);
    yield takeEvery(ON_LOAD_LOGIN_REQUESTED, onLoadLogin);
    yield takeEvery(LOGOUT_REQUESTED, onLogout);
    yield takeEvery(POST_RESET_PASSWORD_REQUESTED, onResetPassword);
}